<script>
import * as AdsV4Api from '@/api/advertisement-v4';

import WithPaginationAndFilters from '@/components/common/paginations/WithPaginationAndFilters.vue';
import CampaignsTable from '../common/CampaignsTable.vue';
import CampaignsTableFilters from './CampaignsTableFilters.vue';
import IconButton from '@/components/ui/IconButton.vue';

export default {
  name: 'OverviewCampaignsTable',
  components: {
    CampaignsTable,
    WithPaginationAndFilters,
    CampaignsTableFilters,
    IconButton,
  },
  props: {
    app: {
      type: String,
      default: '',
    },
  },
  data() {
    return {
      campaignsTableFilters: { type: null, status: null },
      campaigns: [],
      isLoadingCampaigns: !this.app,
      isLoadingStats: false,
      isFullscreen: false,
      campaignsFailed: false,
      downloadingReport: false,
    };
  },
  computed: {
    filteredCampaigns() {
      const { type, status } = this.campaignsTableFilters;
      return this.campaigns.filter(campaign => {
        if (type && type !== campaign.campaignType) return false;
        if (status && status !== campaign.status) return false;
        return true;
      });
    },
  },
  watch: {
    app: {
      immediate: true,
      handler() {
        if (!this.app) return;
        this.fetchTableData();
      },
    },
  },
  methods: {
    async fetchTableData() {
      await this.fetchPackageCampaigns();
      await this.fetchCampaignsStats();
    },
    async fetchPackageCampaigns() {
      try {
        this.isLoadingCampaigns = true;
        this.campaignsFailed = false;
        this.campaigns = await AdsV4Api.getCampaigns({
          packageName: this.app,
        });
      } catch (e) {
        this.$message.error(e.message);
        this.campaignsFailed = true;
      } finally {
        this.isLoadingCampaigns = false;
      }
    },
    async fetchCampaignsStats() {
      try {
        if (this.isLoadingStats) return;
        this.isLoadingStats = true;
        const statsByUUID = await AdsV4Api.getCampaignsOverallStats({
          packageName: this.app,
        });
        this.campaigns = this.campaigns.map(campaign => {
          const campaignStats = statsByUUID[campaign.uuid];
          return { ...campaign, ...campaignStats };
        });
      } catch (e) {
        this.$message.error(e.message);
      } finally {
        this.isLoadingStats = false;
      }
    },
    async downloadExcel() {
      try {
        this.downloadingReport = true;
        const requestParams = {
          packageName: this.app,
          // start: this.selectedDateRange?.[0],
          // end: this.selectedDateRange?.[1],
        };
        await AdsV4Api.downloadOverviewStats(requestParams);
      } catch (e) {
        this.$message.error(e.message);
      } finally {
        this.downloadingReport = false;
      }
    },
    async pauseCampaign(id) {
      try {
        await this.$confirm(
          this.$t('ADVERTISEMENT.CAMPAIGNS.ACTIVATE_GUIDE'),
          this.$t('ADVERTISEMENT.CAMPAIGNS.PAUSE_WARNING'),
          {
            confirmButtonText: this.$t('ADVERTISEMENT.CAMPAIGNS.PAUSE'),
            cancelButtonText: this.$t('CANCEL'),
            confirmButtonClass: 'el-button--danger',
            type: 'danger',
          },
        );
        this.updateCampaign(id, { isPaused: true });
      } catch {
        // nothing
      }
    },
    async activateCampaign(id) {
      this.updateCampaign(id, { isPaused: false });
    },
    async updateCampaign(id, data) {
      try {
        this.campaignsLoading = true;
        await AdsV4Api.updateCampaign(id, data);
        this.$emit('change');
      } catch (e) {
        this.$message.error(e.message);
      } finally {
        this.campaignsLoading = false;
      }
    },
  },
};
</script>

<template>
  <div class="overview-page__campaigns">
    <AlertWithRetry v-if="campaignsFailed" @retry="fetchTableData" />
    <div
      class="overview-campaigns-table"
      :class="{ 'overview-campaigns-table--fullscreen': isFullscreen }"
    >
      <div class="flex-row">
        <div class="flex-grow">
          <CampaignsTableFilters
            :filters.sync="campaignsTableFilters"
            class="mb"
          />
        </div>

        <div class="flex-row gap-sm">
          <!-- <IconButton
            :tooltip="$t('ADVERTISEMENT.BIDS.CHANGE_COLUMN')"
            type="outlined"
          >
            <i class="material-icons iconic-button__icon">view_column</i>
          </IconButton> -->

          <IconButton
            :tooltip="$t('ADVERTISEMENT.ENTER_FULL_SCREEN')"
            type="outlined"
            icon="el-icon-full-screen"
            @click="isFullscreen = !isFullscreen"
          />
          <!-- <IconButton
            :tooltip="$t('ADVERTISEMENT.REPORTING.DOWNLOAD_REPORT')"
            icon="el-icon-download"
            :loading="downloadingReport"
            @click="downloadExcel"
          /> -->
        </div>
      </div>

      <WithPaginationAndFilters
        v-slot="{ data: currentPageData, sortChange }"
        :data="filteredCampaigns"
        :is-raised="false"
      >
        <CampaignsTable
          v-loading="isLoadingCampaigns"
          :campaigns="currentPageData"
          :stats-loading="isLoadingStats"
          show-actions
          class="mb"
          @pause-campaign="pauseCampaign"
          @activate-campaign="activateCampaign"
          @sort-change="sortChange"
        />
      </WithPaginationAndFilters>
    </div>
  </div>
</template>

<style lang="scss">
.overview-campaigns-table {
  &--fullscreen {
    height: 100vh;
    position: fixed;
    z-index: 9999;
    top: 0;
    left: 0;
    right: 0;
    overflow-y: auto;
    padding: 3rem;
    background-color: $lb-white;
  }
}
</style>
